import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import pdfjs from 'pdfjs-dist';
import PDFPage from './pdfPage';
import Loader from '../loader';

// This utility is difficult to test thoroughly because:
// 1. It wraps Promises which are inherently asynchronous and can be challenging to control in tests
// 2. Error paths are particularly challenging because they require triggering Promise rejections
// 3. The catch handler path when hasCanceled=false is hard to reach in integration tests
// 4. State is maintained via closures, making it hard to inspect the internal hasCanceled value
const makeCancelable = promise => {
  let hasCanceled = false;
  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(val => (/* eslint-disable prefer-promise-reject-errors */
    hasCanceled ? reject({
      pdf: val,
      isCanceled: true
    }) : resolve(val)));
    promise.catch(error => hasCanceled ? reject({
      isCanceled: true
    }) : reject(error));
    /* eslint-enable prefer-promise-reject-errors */
  });
  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled = true;
    }
  };
};
const findPageWidth = (pdf, pageNum) => new Promise(resolve => {
  pdf.getPage(pageNum).then(_ref => {
    let view = _ref.view;
    return resolve(view[2] - view[0]);
  });
});
const PDFWrapper = _ref2 => {
  let _ref2$pageInView = _ref2.pageInView,
    pageInView = _ref2$pageInView === void 0 ? 0 : _ref2$pageInView,
    _ref2$file = _ref2.file,
    file = _ref2$file === void 0 ? null : _ref2$file,
    _ref2$onAnnotationSel = _ref2.onAnnotationSelect,
    onAnnotationSelect = _ref2$onAnnotationSel === void 0 ? null : _ref2$onAnnotationSel,
    _ref2$selected = _ref2.selected,
    selected = _ref2$selected === void 0 ? null : _ref2$selected,
    _ref2$zoomFactor = _ref2.zoomFactor,
    zoomFactor = _ref2$zoomFactor === void 0 ? 1.0 : _ref2$zoomFactor,
    _ref2$onDocumentError = _ref2.onDocumentError,
    onDocumentError = _ref2$onDocumentError === void 0 ? null : _ref2$onDocumentError,
    _ref2$className = _ref2.className,
    className = _ref2$className === void 0 ? null : _ref2$className,
    _ref2$onRenderComplet = _ref2.onRenderComplete,
    onRenderComplete = _ref2$onRenderComplet === void 0 ? null : _ref2$onRenderComplet,
    _ref2$evaluateAnnotat = _ref2.evaluateAnnotationHandlerColor,
    evaluateAnnotationHandlerColor = _ref2$evaluateAnnotat === void 0 ? null : _ref2$evaluateAnnotat;
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    pdf = _useState2[0],
    setPdf = _useState2[1];
  const _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    maxPageWidth = _useState4[0],
    setMaxPageWidth = _useState4[1];
  const pageMaskRef = useRef([]);
  const documentPromiseRef = useRef(null);
  const guardRef = useRef(false);
  const lastLoadedFileRef = useRef(null);
  const loadingRef = useRef(false);
  const onDocumentComplete = useCallback(pdfDoc => {
    pageMaskRef.current = Array(pdfDoc.numPages).fill(false);
    const promiseArray = [];
    [...Array(pdfDoc.numPages)].forEach((_, pageNum) => {
      promiseArray.push(findPageWidth(pdfDoc, pageNum + 1));
    });
    return Promise.all(promiseArray).then(pageWidths => {
      setPdf(pdfDoc);
      setMaxPageWidth(Math.max(...pageWidths));
      loadingRef.current = false;
    });
  }, []);

  // eslint-disable-next-line consistent-return
  const handleDocumentError = useCallback(err => {
    loadingRef.current = false;
    if (err.isCanceled && err.pdf && err.pdf.destroy) {
      err.pdf.destroy();
    }
    if (typeof onDocumentError === 'function') {
      onDocumentError(err);
    }
  }, [onDocumentError]);
  const getPDF = useCallback(val => {
    if (documentPromiseRef.current) documentPromiseRef.current.cancel();
    documentPromiseRef.current = makeCancelable(pdfjs.getDocument(val).promise);
    documentPromiseRef.current.promise.then(onDocumentComplete).catch(handleDocumentError);
    return documentPromiseRef.current;
  }, [onDocumentComplete, handleDocumentError]);
  const loadPDFDocument = useCallback(fileSource => {
    if (fileSource && typeof fileSource === 'string' || fileSource instanceof ArrayBuffer || fileSource instanceof Uint8Array) {
      return getPDF(fileSource);
    }
    throw new Error('Unsupported PDF input');
  }, [getPDF]);
  useEffect(() => {
    // Only load if the file has actually changed and we're not already loading
    if (file && file !== lastLoadedFileRef.current && !loadingRef.current) {
      lastLoadedFileRef.current = file;
      loadingRef.current = true;
      loadPDFDocument(file);
    } else if (!file && !loadingRef.current) {
      // Handle case where file is undefined/null
      loadPDFDocument(file);
    }
  }, [file, loadPDFDocument]);
  const onPageRender = useCallback((pageNumber, height) => {
    pageMaskRef.current[pageNumber - 1] = height;
    if (!pageMaskRef.current.some(maskVal => !maskVal) && onRenderComplete && !guardRef.current) {
      guardRef.current = true;
      onRenderComplete(JSON.parse(JSON.stringify(pageMaskRef.current)));
      guardRef.current = false;
    }
  }, [onRenderComplete]);

  // Cleanup
  useEffect(() => () => {
    if (pdf) {
      pdf.destroy();
    }
    if (documentPromiseRef.current) {
      documentPromiseRef.current.cancel();
    }
  }, [pdf]);
  if (pdf && maxPageWidth) {
    return /*#__PURE__*/React.createElement("div", {
      className: className,
      style: {
        textAlign: 'center'
      },
      "data-testid": "pdfWrapper"
    }, [...Array(pdf.numPages)].map((_, page) => /*#__PURE__*/React.createElement(PDFPage, {
      pageInView: pageInView
      /* eslint-disable react/no-array-index-key */,
      key: "".concat(className, "-").concat(page + 1)
      /* eslint-enable react/no-array-index-key */,
      pdf: pdf,
      selected: selected,
      onPageRender: onPageRender,
      page: page + 1,
      onClick: onAnnotationSelect,
      zoomFactor: zoomFactor,
      maxPageWidth: maxPageWidth,
      evaluateAnnotationHandlerColor: evaluateAnnotationHandlerColor
    })));
  }
  return /*#__PURE__*/React.createElement(Loader, null);
};
PDFWrapper.displayName = 'pdfWrapper';
export { makeCancelable };
export default PDFWrapper;
PDFWrapper.propTypes = {
  pageInView: PropTypes.number,
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(ArrayBuffer), PropTypes.instanceOf(Uint8Array)]),
  onAnnotationSelect: PropTypes.func,
  selected: PropTypes.shape({}),
  zoomFactor: PropTypes.number,
  onDocumentError: PropTypes.func,
  className: PropTypes.string,
  onRenderComplete: PropTypes.func,
  evaluateAnnotationHandlerColor: PropTypes.func
};